import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Footer from '../components/layout/Footer';
import classService from '../services/classService';
import { getLocalizedUiConfig, getDisplayDuration, formatClassLocationDisplay, formatClassPrice, formatSessionLocationDisplay, localizeClassItem, isOrientationClass } from '../config/classUiConfig';
import enrollmentService from '../services/enrollmentService';
import { useNotifications } from '../utils/notificationUtils';
import { formatClassDate, formatClassTimeRange } from '../utils/nyDateTime';
import EnrollmentFormModal from '../components/enrollment/EnrollmentFormModal';

function ClassDetails() {
    const { t, i18n } = useTranslation('classes');
    const { id } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading, initialized } = useAuth();
    const { showSuccess, showError } = useNotifications();
    const [classData, setClassData] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEnrolled, setIsEnrolled] = useState(false);
    const [enrollError, setEnrollError] = useState('');
    const [roleEnrollError, setRoleEnrollError] = useState('');
    const [enrollSuccess, setEnrollSuccess] = useState('');
    const [enrollLoading, setEnrollLoading] = useState(false);
    const [selectedDateIndex, setSelectedDateIndex] = useState(null);
    const [waitlistStatus, setWaitlistStatus] = useState({});
    const [waitlistLoading, setWaitlistLoading] = useState(false);
    const [userEnrollments, setUserEnrollments] = useState([]);
    const [userWaitlist, setUserWaitlist] = useState({});
    const [enrollmentModalOpen, setEnrollmentModalOpen] = useState(false);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [enrollmentError, setEnrollmentError] = useState('');
    const [enrollmentSuccess, setEnrollmentSuccess] = useState('');
    const [enrollingSessionId, setEnrollingSessionId] = useState(null);
    const isAdminOrInstructor = user && (user.role === 'admin' || user.role === 'instructor');
    const skipEnrollmentForm = isOrientationClass(classData?.title);

    // Memoize the class data fetching effect
    useEffect(() => {
        const fetchClassDetails = async () => {
            if (!initialized) return;
            setLoading(true);
            setError(null);
            try {
                const data = await classService.getClassById(id);
                setClassData(data);
                // Fetch sessions for this class
                const sessionData = await classService.getClassSessions(id);
                setSessions(sessionData);

                // Only check enrollment and waitlist status if user is logged in
                if (user) {
                    try {
                        const enrollments = await enrollmentService.getUserEnrollments();
                        // Check if user has pending or approved enrollment in this class for a FUTURE session
                        // Only block if enrollment is active (future session), not historical (past)
                        // Convert both to strings for comparison to handle type mismatch
                        const activeEnrollment = enrollments.find(
                            enrollment => String(enrollment.class_id) === String(id) &&
                                (enrollment.enrollment_status === 'pending' || enrollment.enrollment_status === 'approved') &&
                                enrollment.enrollment_type === 'active' // Only block active (future) enrollments
                        );
                        const isUserEnrolled = !!activeEnrollment;
                        setIsEnrolled(isUserEnrolled);
                        setUserEnrollments(enrollments);

                        // Check waitlist status for each session
                        const sessionWaitlistStatus = {};
                        for (const session of sessionData) {
                            if (session.available_spots === 0) {
                                try {
                                    const waitlistData = await enrollmentService.getWaitlistStatus(id, session.id);
                                    sessionWaitlistStatus[session.id] = waitlistData;
                                } catch (err) {
                                    // Handle 404 'Not on waitlist' gracefully without logging
                                    if (err.message === 'Not on waitlist' ||
                                        (err.response?.status === 404 && err.message === 'Not on waitlist')) {
                                        // Not on waitlist for this session - this is expected
                                        sessionWaitlistStatus[session.id] = null;
                                    } else {
                                        console.error(`Error fetching waitlist status for session ${session.id}:`, err);
                                    }
                                }
                            }
                        }
                        setWaitlistStatus(sessionWaitlistStatus);
                        setUserWaitlist(sessionWaitlistStatus);
                    } catch (err) {
                        // Only log if not a waitlist-related error
                        if (err.message !== 'Not on waitlist' &&
                            !(err.response?.status === 404 && err.message === 'Not on waitlist')) {
                            console.error('Error fetching user enrollment data:', err);
                        }
                        // Don't set error state for enrollment data, as it's not critical
                    }
                }
            } catch (err) {
                setError(err.message || t('details.loadError'));
            } finally {
                setLoading(false);
            }
        };

        fetchClassDetails();
    }, [id, user, initialized]);

    const handleEnroll = async (sessionId) => {
        if (!user) {
            navigate('/login', { state: { from: `/classes/${id}` } });
            return;
        }
        if (user.role === 'admin' || user.role === 'instructor') {
            setRoleEnrollError(t('details.adminCannotEnrollError'));
            return;
        }

        if (skipEnrollmentForm) {
            setEnrollingSessionId(sessionId);
            setEnrollLoading(true);
            try {
                await enrollmentService.enrollInClass(id, { sessionId });
                setIsEnrolled(true);
                const enrollments = await enrollmentService.getUserEnrollments();
                setUserEnrollments(enrollments);
                showSuccess(t('details.enrollSuccess'));
            } catch (err) {
                showError(err.message || t('details.enrollFailed'));
            } finally {
                setEnrollLoading(false);
                setEnrollingSessionId(null);
            }
            return;
        }

        setSelectedSessionId(sessionId);
        setEnrollmentModalOpen(true);
    };

    const handleEnrollSubmit = async (sessionId, paymentMethod) => {
        setEnrollLoading(true);
        setEnrollmentError('');
        setEnrollmentSuccess('');
        try {
            await enrollmentService.enrollInClass(id, { sessionId, paymentMethod });
            setEnrollmentModalOpen(false);
            setSelectedSessionId(null);
            setEnrollmentSuccess('');
            setEnrollmentError('');
            setIsEnrolled(true);

            try {
                const enrollments = await enrollmentService.getUserEnrollments();
                setUserEnrollments(enrollments);
            } catch (refreshError) {
                console.error('Failed to refresh enrollments after signup:', refreshError);
            }

            showSuccess(t('details.enrollSuccess'));
        } catch (err) {
            setEnrollmentError(err.message || t('details.enrollFailed'));
            console.error('Enrollment error:', err);
        } finally {
            setEnrollLoading(false);
        }
    };

    const handleCloseEnrollmentModal = () => {
        setEnrollmentModalOpen(false);
        setSelectedSessionId(null);
        setEnrollmentError('');
        setEnrollmentSuccess('');
    };

    const handleWaitlistAction = async (sessionId) => {
        if (!user) {
            navigate('/login', { state: { from: `/classes/${id}` } });
            return;
        }

        setWaitlistLoading(true);
        try {
            if (waitlistStatus[sessionId]) {
                await enrollmentService.leaveWaitlist(id, sessionId);
                setWaitlistStatus({ ...waitlistStatus, [sessionId]: null });
                showSuccess(t('details.waitlistRemoved'));
            } else {
                const result = await enrollmentService.joinWaitlist(id, sessionId);
                setWaitlistStatus({ ...waitlistStatus, [sessionId]: result });
                showSuccess(t('details.waitlistAdded'));
            }
        } catch (err) {
            showError(err.message || t('details.waitlistFailed'));
        } finally {
            setWaitlistLoading(false);
        }
    };

    const localizedClass = useMemo(
        () => localizeClassItem(classData, i18n.language),
        [classData, i18n.language]
    );

    const uiConfig = useMemo(
        () => getLocalizedUiConfig(classData?.title, i18n.language),
        [classData?.title, i18n.language]
    );

    // Show loading state while auth is initializing or class data is loading
    if (authLoading || !initialized || loading) {
        return (
            <div className="min-h-screen bg-white font-montserrat">
                <div className="flex items-center justify-center h-[60vh]">
                    <p className="text-xl text-gray-600">Loading class details...</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-white font-montserrat">
                <div className="flex items-center justify-center h-[60vh]">
                    <p className="text-xl text-red-600">{error}</p>
                </div>
                <Footer />
            </div>
        );
    }

    if (!classData) {
        return (
            <div className="min-h-screen bg-white font-montserrat">
                <div className="flex items-center justify-center h-[60vh]">
                    <p className="text-xl text-gray-600">{t('details.notFound')}</p>
                </div>
                <Footer />
            </div>
        );
    }

    const spanishSessions = sessions.filter(
        (session) => (session.language || 'english').toLowerCase() === 'spanish'
    );
    const englishSessions = sessions.filter(
        (session) => (session.language || 'english').toLowerCase() !== 'spanish'
    );

    const renderSessionCard = (session) => {
        const sessionDate = session.start_date || session.session_date;
        const formattedStartDate = formatClassDate(sessionDate, i18n.language);
        const formattedEndDate = session.end_date ? formatClassDate(session.end_date, i18n.language) : null;
        const showEndDate = formattedEndDate && formattedEndDate !== formattedStartDate;
        const notLoggedIn = !user;
        const isAdminOrInstructorUser = user && (user.role === 'admin' || user.role === 'instructor');
        const hasActiveEnrollment = userEnrollments.some(
            enrollment => String(enrollment.class_id) === String(id) &&
                (enrollment.enrollment_status === 'pending' || enrollment.enrollment_status === 'approved') &&
                enrollment.enrollment_type === 'active'
        );
        const canEnroll = !notLoggedIn && !isAdminOrInstructorUser && !hasActiveEnrollment;

        return (
            <div key={session.id} className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="space-y-3">
                    <div className="font-semibold text-lg text-gray-900">
                        <span className="font-medium text-gray-600">{t('details.start')}</span> {formattedStartDate}
                    </div>
                    {showEndDate && (
                        <div className="text-gray-700">
                            <span className="font-medium">{t('details.end')}</span> {formattedEndDate}
                        </div>
                    )}
                    <div className="text-gray-700">
                        <span className="font-medium">{t('details.time')}</span>{' '}
                        {formatClassTimeRange(session.start_time, session.end_time, i18n.language, sessionDate)}
                    </div>
                    <div className="text-gray-700">
                        <span className="font-medium">{t('details.instructor')}</span> {session.instructor_name || t('details.tba')}
                    </div>
                    <div className="text-gray-700">
                        <span className="font-medium">{t('details.location')}</span>{' '}
                        {formatSessionLocationDisplay(
                            session,
                            i18n.language,
                            localizedClass?.location_details
                        ) || t('details.tba')}
                    </div>
                </div>
                <div className="mt-4 flex justify-start">
                    <button
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${canEnroll || notLoggedIn
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                        disabled={(!canEnroll && !notLoggedIn) || (enrollLoading && enrollingSessionId === session.id)}
                        onClick={() => (canEnroll ? handleEnroll(session.id) : notLoggedIn && navigate('/login', { state: { from: `/classes/${id}` } }))}
                    >
                        {enrollLoading && enrollingSessionId === session.id ? t('details.enrolling') : t('details.enrollNow')}
                    </button>
                </div>
                {notLoggedIn && <div className="text-sm text-gray-600 mt-2">{t('details.loginToEnroll')}</div>}
                {isAdminOrInstructorUser && <div className="text-sm text-red-500 mt-2">{t('details.adminCannotEnroll')}</div>}
                {hasActiveEnrollment && (
                    <div className="text-sm text-yellow-600 mt-2">
                        {userEnrollments.find(e => String(e.class_id) === String(id) && (e.enrollment_status === 'pending' || e.enrollment_status === 'approved') && e.enrollment_type === 'active')?.enrollment_status === 'pending'
                            ? t('details.pendingEnrollment')
                            : t('details.alreadyEnrolled')}
                    </div>
                )}
                {session.available_spots === 0 && canEnroll && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                        <p className="text-sm text-yellow-800 mb-2">{t('details.sessionFull')}</p>
                        {waitlistStatus[session.id] ? (
                            <div className="space-y-2">
                                <p className="text-xs text-yellow-700">{t('details.onWaitlist')}</p>
                                <button
                                    className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                                    onClick={() => handleWaitlistAction(session.id)}
                                    disabled={waitlistLoading}
                                >
                                    {waitlistLoading ? t('details.removing') : t('details.leaveWaitlist')}
                                </button>
                            </div>
                        ) : (
                            <button
                                className="px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700"
                                onClick={() => handleWaitlistAction(session.id)}
                                disabled={waitlistLoading}
                            >
                                {waitlistLoading ? t('details.adding') : t('details.joinWaitlist')}
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderSessionSection = (title, sectionSessions) => (
        <div className="mb-10 last:mb-0">
            <h3 className="text-xl font-bold mb-4 text-gray-900 uppercase tracking-wide">{title}</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {sectionSessions.map((session) => renderSessionCard(session))}
            </div>
        </div>
    );

    const renderOnSiteTraining = (onSite) => (
        <div>
            <h3 className="text-2xl font-semibold mb-6 text-gray-900">{onSite.title}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">{onSite.intro}</p>
            <div className="space-y-6 mb-8">
                {onSite.courses?.map((course) => (
                    <div key={course.title}>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">{course.title}</h4>
                        <p className="text-gray-700 leading-relaxed">{course.content}</p>
                        <p className="text-gray-700 leading-relaxed mt-2">{course.certification}</p>
                    </div>
                ))}
            </div>
            {onSite.listItems?.length > 0 && (
                <ul className="list-disc list-inside text-red-800 space-y-2">
                    {onSite.listItems.map((item) => (
                        <li key={item}>{item}</li>
                    ))}
                </ul>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-white font-montserrat">
            {/* Hero Section */}
            <section className="relative w-full h-[400px] flex items-center justify-center text-white text-center overflow-hidden mb-0 px-6" style={{ margin: '10px auto' }}>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-800 z-0" />
                <div className="relative z-20 flex flex-col items-center justify-center w-full h-full">
                    <h1 className="text-4xl md:text-5xl font-normal mb-4">{localizedClass.title}</h1>
                    <p className="text-lg md:text-xl max-w-2xl mx-auto opacity-90">
                        {uiConfig.heroSubtitle || getDisplayDuration(localizedClass, i18n.language)}
                    </p>
                </div>
            </section>

            {/* Divider */}
            <div className="w-full border-t border-gray-200 my-0" />

            {/* Class Details */}
            <section className="py-16 max-w-6xl mx-auto px-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    {/* Left Column - Class Information */}
                    <div className="space-y-8">
                        <div>
                            <h2 className="text-2xl font-semibold mb-4 text-gray-900">{t('details.aboutClass')}</h2>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-line">{localizedClass.description}</p>
                        </div>

                        {uiConfig.sections?.map((section) => (
                            <div key={section.title}>
                                <h2 className="text-2xl font-semibold mb-4 text-gray-900">{section.title}</h2>
                                <p className="text-gray-700 leading-relaxed">{section.content}</p>
                                {section.listItems?.length > 0 && (
                                    section.numberedList ? (
                                        <ol className="list-decimal list-outside text-gray-700 space-y-3 mt-3 ml-6 pl-2">
                                            {section.listItems.map((item, index) => (
                                                <li key={`${section.title}-${index}`} className="pl-1">
                                                    {item}
                                                </li>
                                            ))}
                                        </ol>
                                    ) : (
                                        <ul className="list-disc list-inside text-gray-700 space-y-2 mt-3">
                                            {section.listItems.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    )
                                )}
                                {section.link && (
                                    <a
                                        href={section.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-block mt-2 text-blue-600 hover:text-blue-800 underline"
                                    >
                                        {section.linkLabel || section.link}
                                    </a>
                                )}
                            </div>
                        ))}

                        {/* Location Information */}
                        <div>
                            <h2 className="text-2xl font-semibold mb-4 text-gray-900">{t('details.locationInfo')}</h2>
                            <p className="text-gray-700">
                                {formatClassLocationDisplay(localizedClass, i18n.language, uiConfig) || t('details.seeSessionDetails')}
                            </p>
                        </div>

                        {/* Enrollment Card - Mobile Only */}
                        <div className="md:hidden">
                            <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                                <div className="space-y-4">
                                    <div>
                                        <h2 className="text-xl font-semibold mb-2 text-gray-900">{t('details.enrollment')}</h2>
                                        <p className="text-2xl font-semibold text-blue-600">{formatClassPrice(localizedClass.price, i18n.language)}</p>
                                    </div>

                                    <div className="space-y-3">
                                        <div className="flex items-center space-x-2">
                                            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span className="text-sm text-gray-700">{t('details.certificate')}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span className="text-sm text-gray-700">{t('details.expertInstruction')}</span>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                            </svg>
                                            <span className="text-sm text-gray-700">{t('details.handsOn')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Right Column - Enrollment Card - Desktop Only */}
                    <div className="hidden md:block bg-gray-50 p-8 rounded-lg shadow-sm">
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-2xl font-semibold mb-2 text-gray-900">{t('details.enrollment')}</h2>
                                <p className="text-3xl font-semibold text-blue-600">{formatClassPrice(localizedClass.price, i18n.language)}</p>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center space-x-2">
                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-gray-700">{t('details.certificate')}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-gray-700">{t('details.expertInstruction')}</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                    </svg>
                                    <span className="text-gray-700">{t('details.handsOn')}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {(sessions.length > 0 || uiConfig.onSiteTraining) && (
                    <div className="mt-16 border-t border-gray-200 pt-12">
                        {uiConfig.onSiteTraining && renderOnSiteTraining(uiConfig.onSiteTraining)}
                        {sessions.length > 0 && (
                            <div className={uiConfig.onSiteTraining ? 'mt-10 pt-10 border-t border-gray-200' : ''}>
                                <h2 className="text-2xl font-semibold mb-2 text-gray-900">{t('details.availableSessions')}</h2>
                                <p className="text-sm text-gray-600 mb-6">{t('details.easternTimeNote')}</p>
                                {spanishSessions.length > 0 && renderSessionSection(t('details.spanishSessions'), spanishSessions)}
                                {englishSessions.length > 0 && renderSessionSection(t('details.englishSessions'), englishSessions)}
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Call to Action */}
            <section className="bg-gray-100 py-16 px-6">
                <div className="max-w-2xl mx-auto text-center">
                    <h2 className="text-3xl font-semibold mb-4 text-gray-900">{t('details.ctaTitle')}</h2>
                    <p className="mb-8 text-gray-700">{t('details.ctaDescription')}</p>
                </div>
            </section>

            {/* Show error if admin/instructor tries to enroll */}
            {roleEnrollError && (
                <div className="mb-4 p-2 bg-red-50 border border-red-200 text-red-700 rounded flex items-center justify-between">
                    <span>{roleEnrollError}</span>
                    <button
                        onClick={() => setRoleEnrollError('')}
                        className="ml-4 text-red-700 hover:text-red-900 font-bold text-lg focus:outline-none"
                        aria-label={t('details.closeError')}
                    >
                        ×
                    </button>
                </div>
            )}

            {!skipEnrollmentForm && enrollmentModalOpen && (
                <EnrollmentFormModal
                    open={enrollmentModalOpen}
                    onClose={handleCloseEnrollmentModal}
                    onEnroll={handleEnrollSubmit}
                    sessionId={selectedSessionId}
                    loading={enrollLoading}
                    enrollmentError={enrollmentError}
                    enrollmentSuccess={enrollmentSuccess}
                />
            )}

            <Footer />
        </div>
    );
}

const MemoizedClassDetails = React.memo(ClassDetails);
export default MemoizedClassDetails; 